'use client';
import type { ElementType } from 'react';
import { Link } from '@skin-seoul/design-system';
import { useLoyaltyService, useUser, parseMessageWithBold, type Country, type Currency } from '@skin-seoul/service';
import { useCartDrawer } from '../../../context/CartDrawerContext';
import { getAuthenticatedMilestoneBannerMessage } from '../constants/milestoneBanner';
import { getSignInBannerSuffix } from '../constants/signInBanner';
import { LoyaltyPointsBannerSkeleton } from './LoyaltyPointsBannerSkeleton';

type LoyaltyPointsBannerContainerProps = {
  linkComponent?: ElementType;
  cartTotalPrice: number;
  country: Country;
  currency: Currency;
  isFirstOrder: boolean;
};

export const LoyaltyPointsBannerContainer = ({
  linkComponent,
  cartTotalPrice,
  country,
  currency,
  isFirstOrder,
}: LoyaltyPointsBannerContainerProps) => {
  const LinkComponent = linkComponent || Link;
  const { onCartClose } = useCartDrawer();
  const { user, isAuthenticated, isLoading: isUserLoading } = useUser();
  const { useGetCustomerBalance } = useLoyaltyService();

  const { data: balanceData, isLoading: isBalanceLoading } = useGetCustomerBalance({
    customerId: String(user?.userId ?? ''),
    currency,
    enabled: isAuthenticated && Boolean(user?.userId),
  });

  // Show skeleton while loading user or balance data
  if (isUserLoading || isBalanceLoading) {
    return <LoyaltyPointsBannerSkeleton />;
  }

  // Show sign-in banner if not authenticated or no data
  if (!isAuthenticated || !user?.userId || !balanceData) {
    const suffix = getSignInBannerSuffix(cartTotalPrice, country);
    return (
      <div className="w-full bg-primary-75 p-1 text-center rounded-b-[12px]">
        <span className="text-label-medium font-medium text-primary-500">
          <LinkComponent unstyled href="/login" className="underline font-semibold text-primary-500" onClick={onCartClose}>
            Sign in
          </LinkComponent>
          {suffix}
        </span>
      </div>
    );
  }

  const milestoneMessage = getAuthenticatedMilestoneBannerMessage({
    cartTotalPrice,
    country,
    currency,
    isFirstOrder,
  });
  if (milestoneMessage) {
    return (
      <div className="w-full bg-primary-75 p-1 text-center rounded-b-[12px]">
        <span className="text-label-medium font-medium text-primary-500">
          {parseMessageWithBold(milestoneMessage).map((part, i) => (
            <span key={i} className={part.bold ? 'font-semibold' : 'font-medium'}>
              {part.text}
            </span>
          ))}
        </span>
      </div>
    );
  }

  return null;
};
